import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Building2, Users, Leaf, Package, Download, QrCode, Link2, LogOut,
  BarChart3, Calendar, FileText, Upload, Eye, Trash2, RefreshCw,
  ChevronDown, ChevronUp, PieChart, TrendingUp, Award
} from 'lucide-react';
import { toast } from 'sonner';
import QRCode from 'qrcode';
import jsPDF from 'jspdf';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const APP_URL = 'https://respiro-dashboard.emergent.host';

// Colori per grafico a torta
const CHART_COLORS = [
  '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#6366f1'
];

const OrganizationPortal = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orgData, setOrgData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [showMembers, setShowMembers] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  
  // Login form
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  
  // PDF date range
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  // Check if already logged in
  useEffect(() => {
    const token = localStorage.getItem('org_token');
    if (token) {
      fetchDashboard(token);
    }
  }, []);

  // Login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/org/login`, loginForm);
      
      localStorage.setItem('org_token', response.data.token);
      localStorage.setItem('org_id', response.data.organization_id);
      localStorage.setItem('org_name', response.data.organization_name);
      
      setIsLoggedIn(true);
      toast.success(`Benvenuto, ${response.data.organization_name}!`);
      
      fetchDashboard(response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore di login');
    } finally {
      setLoading(false);
    }
  };

  // Logout
  const handleLogout = () => {
    localStorage.removeItem('org_token');
    localStorage.removeItem('org_id');
    localStorage.removeItem('org_name');
    setIsLoggedIn(false);
    setOrgData(null);
    setDocuments([]);
    toast.info('Disconnesso');
  };

  // Fetch dashboard data
  const fetchDashboard = useCallback(async (token) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/org/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOrgData(response.data);
      setIsLoggedIn(true);
      
      // Generate QR code
      const inviteUrl = `${APP_URL}/join/${response.data.invite_code}`;
      const qr = await QRCode.toDataURL(inviteUrl, {
        width: 200,
        margin: 2,
        color: { dark: '#0891b2', light: '#ffffff' }
      });
      setQrCodeUrl(qr);
      
      // Fetch documents
      fetchDocuments(token);
    } catch (error) {
      console.error('Dashboard error:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch documents
  const fetchDocuments = async (token) => {
    try {
      const response = await axios.get(`${API_URL}/api/org/documents`, {
        headers: { Authorization: `Bearer ${token || localStorage.getItem('org_token')}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Documents error:', error);
    }
  };

  // Copy invite link
  const copyInviteLink = () => {
    const link = `${APP_URL}/join/${orgData.invite_code}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copiato!');
  };

  // Download QR code
  const downloadQRCode = () => {
    if (!qrCodeUrl) return;
    
    const link = document.createElement('a');
    link.download = `QR_${orgData.organization_name.replace(/\s+/g, '_')}.png`;
    link.href = qrCodeUrl;
    link.click();
    toast.success('QR Code scaricato!');
  };

  // Generate PDF report
  const generatePDFReport = async () => {
    try {
      toast.info('Generazione report in corso...');
      
      const token = localStorage.getItem('org_token');
      const response = await axios.get(`${API_URL}/api/org/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date: dateRange.start,
          end_date: dateRange.end
        }
      });
      
      const data = response.data;
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Header con logo
      if (data.organization_logo) {
        try {
          doc.addImage(data.organization_logo, 'PNG', 15, 10, 40, 40);
        } catch (e) {
          console.log('Logo error:', e);
        }
      }
      
      // Titolo
      doc.setFontSize(22);
      doc.setTextColor(8, 145, 178); // cyan-600
      doc.text('REPORT COMMUNITY', pageWidth / 2, 25, { align: 'center' });
      
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text(data.organization_name, pageWidth / 2, 35, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Periodo: ${data.period.start} - ${data.period.end}`, pageWidth / 2, 45, { align: 'center' });
      
      // Linea separatrice
      doc.setDrawColor(8, 145, 178);
      doc.setLineWidth(0.5);
      doc.line(15, 55, pageWidth - 15, 55);
      
      // Statistiche box
      const boxY = 65;
      doc.setFillColor(240, 249, 255);
      doc.roundedRect(15, boxY, pageWidth - 30, 45, 5, 5, 'F');
      
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text('IMPATTO DELLA TUA COMMUNITY', pageWidth / 2, boxY + 12, { align: 'center' });
      
      // Stats grid
      const statsY = boxY + 25;
      doc.setFontSize(20);
      doc.setTextColor(8, 145, 178);
      
      const stats = [
        { value: data.stats.members_count, label: 'Membri' },
        { value: `${data.stats.total_kg} kg`, label: 'Raccolti' },
        { value: data.stats.total_collections, label: 'Raccolte' },
        { value: `${data.stats.co2_saved_kg} kg`, label: 'CO₂ Risparmiata' }
      ];
      
      const colWidth = (pageWidth - 30) / 4;
      stats.forEach((stat, i) => {
        const x = 15 + colWidth * i + colWidth / 2;
        doc.setFontSize(18);
        doc.setTextColor(8, 145, 178);
        doc.text(String(stat.value), x, statsY, { align: 'center' });
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text(stat.label, x, statsY + 8, { align: 'center' });
      });
      
      // Grafico a torta (semplificato come lista)
      let yPos = boxY + 60;
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text('MATERIALI RACCOLTI', pageWidth / 2, yPos, { align: 'center' });
      
      yPos += 10;
      const materials = Object.entries(data.materials).filter(([_, v]) => v > 0);
      const totalMaterials = materials.reduce((sum, [_, v]) => sum + v, 0);
      
      if (totalMaterials > 0) {
        materials.forEach(([name, value], i) => {
          const percentage = ((value / totalMaterials) * 100).toFixed(1);
          const barWidth = (value / totalMaterials) * 100;
          
          // Nome e valore
          doc.setFontSize(10);
          doc.setTextColor(60, 60, 60);
          doc.text(`${name}: ${value.toFixed(2)} kg (${percentage}%)`, 20, yPos);
          
          // Barra
          doc.setFillColor(...hexToRgb(CHART_COLORS[i % CHART_COLORS.length]));
          doc.rect(20, yPos + 2, barWidth * 1.5, 4, 'F');
          
          yPos += 12;
        });
      } else {
        doc.setFontSize(10);
        doc.setTextColor(150, 150, 150);
        doc.text('Nessuna raccolta nel periodo selezionato', pageWidth / 2, yPos + 10, { align: 'center' });
      }
      
      // Footer
      yPos = 270;
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text('Respiro Blu - Citizen Science per il Mediterraneo', pageWidth / 2, yPos, { align: 'center' });
      doc.text(`Report generato il ${new Date().toLocaleDateString('it-IT')}`, pageWidth / 2, yPos + 5, { align: 'center' });
      
      // Salva
      doc.save(`Report_${data.organization_name.replace(/\s+/g, '_')}_${dateRange.start}_${dateRange.end}.pdf`);
      toast.success('Report PDF generato!');
      
    } catch (error) {
      console.error('PDF error:', error);
      toast.error('Errore nella generazione del report');
    }
  };

  // Helper: hex to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ] : [0, 0, 0];
  };

  // Render pie chart SVG
  const renderPieChart = () => {
    if (!orgData?.materials) return null;
    
    const materials = Object.entries(orgData.materials).filter(([_, v]) => v > 0);
    const total = materials.reduce((sum, [_, v]) => sum + v, 0);
    
    if (total === 0) {
      return (
        <div className="text-center text-slate-400 py-8">
          <PieChart className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>Nessuna raccolta registrata</p>
        </div>
      );
    }
    
    let currentAngle = 0;
    const paths = materials.map(([name, value], i) => {
      const percentage = value / total;
      const angle = percentage * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;
      currentAngle = endAngle;
      
      const startRad = (startAngle - 90) * Math.PI / 180;
      const endRad = (endAngle - 90) * Math.PI / 180;
      
      const x1 = 50 + 40 * Math.cos(startRad);
      const y1 = 50 + 40 * Math.sin(startRad);
      const x2 = 50 + 40 * Math.cos(endRad);
      const y2 = 50 + 40 * Math.sin(endRad);
      
      const largeArc = angle > 180 ? 1 : 0;
      
      const pathD = `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`;
      
      return (
        <path
          key={name}
          d={pathD}
          fill={CHART_COLORS[i % CHART_COLORS.length]}
          className="hover:opacity-80 transition-opacity cursor-pointer"
        >
          <title>{`${name}: ${value.toFixed(2)} kg (${(percentage * 100).toFixed(1)}%)`}</title>
        </path>
      );
    });
    
    return (
      <div className="flex flex-col items-center">
        <svg viewBox="0 0 100 100" className="w-48 h-48">
          {paths}
        </svg>
        <div className="flex flex-wrap gap-2 mt-4 justify-center">
          {materials.map(([name, value], i) => (
            <div key={name} className="flex items-center gap-1 text-xs">
              <div 
                className="w-3 h-3 rounded-sm" 
                style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
              />
              <span className="text-slate-300">{name}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Login screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center mb-4 shadow-lg">
              <Building2 className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">Portale Organizzazioni</h1>
            <p className="text-cyan-400 mt-1">Respiro Blu</p>
          </div>
          
          <form onSubmit={handleLogin} className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 space-y-4">
            <div>
              <label className="text-sm text-slate-400">Email</label>
              <input
                type="email"
                value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                className="w-full mt-1 px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white focus:border-cyan-500 focus:outline-none"
                placeholder="email@azienda.it"
                required
              />
            </div>
            
            <div>
              <label className="text-sm text-slate-400">Password</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                className="w-full mt-1 px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white focus:border-cyan-500 focus:outline-none"
                placeholder="••••••••"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-bold rounded-xl disabled:opacity-50"
            >
              {loading ? 'Accesso...' : 'Accedi'}
            </button>
          </form>
          
          <p className="text-center text-slate-500 text-sm mt-6">
            Non hai un account? Contatta l'amministratore di Respiro Blu
          </p>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-950 p-4 pb-24">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            {orgData?.organization_logo ? (
              <img 
                src={orgData.organization_logo} 
                alt="Logo" 
                className="w-14 h-14 rounded-xl object-contain bg-white p-1"
              />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center">
                <Building2 className="w-7 h-7 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-xl font-bold text-white">{orgData?.organization_name}</h1>
              <p className="text-cyan-400 text-sm">Portale Community</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchDashboard(localStorage.getItem('org_token'))}
              className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-400"
              title="Aggiorna"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <button
              onClick={handleLogout}
              className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400"
              title="Esci"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-slate-800/50 border border-cyan-500/30 rounded-xl p-4 text-center">
            <Users className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{orgData?.stats?.members_count || 0}</p>
            <p className="text-xs text-slate-400">Membri</p>
          </div>
          
          <div className="bg-slate-800/50 border border-emerald-500/30 rounded-xl p-4 text-center">
            <Package className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{orgData?.stats?.total_kg || 0} <span className="text-sm">kg</span></p>
            <p className="text-xs text-slate-400">Raccolti</p>
          </div>
          
          <div className="bg-slate-800/50 border border-violet-500/30 rounded-xl p-4 text-center">
            <Award className="w-6 h-6 text-violet-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{orgData?.stats?.total_challenges || 0}</p>
            <p className="text-xs text-slate-400">Sfide</p>
          </div>
          
          <div className="bg-slate-800/50 border border-green-500/30 rounded-xl p-4 text-center">
            <Leaf className="w-6 h-6 text-green-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{orgData?.stats?.co2_saved_kg || 0} <span className="text-sm">kg</span></p>
            <p className="text-xs text-slate-400">CO₂ Risparmiata</p>
          </div>
        </div>

        {/* Invite Section */}
        <div className="bg-slate-800/50 border border-cyan-500/30 rounded-xl p-4 mb-6">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Link2 className="w-5 h-5 text-cyan-400" />
            Invita Nuovi Membri
          </h2>
          
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-slate-400 mb-2">Link di invito:</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={`${APP_URL}/join/${orgData?.invite_code || ''}`}
                  readOnly
                  className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm"
                />
                <button
                  onClick={copyInviteLink}
                  className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg"
                >
                  Copia
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Codice: <span className="text-cyan-400 font-mono">{orgData?.invite_code}</span>
              </p>
            </div>
            
            <div className="text-center">
              <p className="text-sm text-slate-400 mb-2">QR Code:</p>
              {qrCodeUrl && (
                <div className="inline-block bg-white p-2 rounded-lg">
                  <img src={qrCodeUrl} alt="QR Code" className="w-32 h-32" />
                </div>
              )}
              <button
                onClick={downloadQRCode}
                className="mt-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm flex items-center gap-2 mx-auto"
              >
                <Download className="w-4 h-4" />
                Scarica QR
              </button>
            </div>
          </div>
        </div>

        {/* Pie Chart */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 mb-6">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5 text-amber-400" />
            Materiali Raccolti
          </h2>
          {renderPieChart()}
        </div>

        {/* PDF Report Generator */}
        <div className="bg-slate-800/50 border border-violet-500/30 rounded-xl p-4 mb-6">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-violet-400" />
            Genera Report PDF
          </h2>
          
          <div className="grid sm:grid-cols-3 gap-4 items-end">
            <div>
              <label className="text-sm text-slate-400">Data inizio</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400">Data fine</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
              />
            </div>
            <button
              onClick={generatePDFReport}
              className="px-6 py-2 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-bold rounded-lg flex items-center justify-center gap-2"
            >
              <Download className="w-5 h-5" />
              Genera PDF
            </button>
          </div>
        </div>

        {/* Members List */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden mb-6">
          <button
            onClick={() => setShowMembers(!showMembers)}
            className="w-full p-4 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
          >
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-400" />
              La Mia Community ({orgData?.members?.length || 0} membri)
            </h2>
            {showMembers ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </button>
          
          {showMembers && (
            <div className="border-t border-slate-700 p-4">
              {orgData?.members?.length > 0 ? (
                <div className="space-y-2">
                  {orgData.members.map((member, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 px-3 bg-slate-900/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400 text-sm font-bold">
                          {(member.name || '?')[0].toUpperCase()}
                        </div>
                        <span className="text-white">{member.name || 'Utente'}</span>
                      </div>
                      <span className="text-xs text-slate-500">{member.user_type || 'volontario'}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-400 py-4">
                  Nessun membro ancora. Condividi il link di invito!
                </p>
              )}
            </div>
          )}
        </div>

        {/* Documents */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-400" />
            Documenti per i tuoi Membri
          </h2>
          
          {documents.length > 0 ? (
            <div className="space-y-2">
              {documents.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between py-3 px-4 bg-slate-900/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">{doc.title}</p>
                    <p className="text-xs text-slate-500">{doc.document_type} • {new Date(doc.created_at).toLocaleDateString('it-IT')}</p>
                  </div>
                  <a
                    href={doc.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-cyan-500/20 hover:bg-cyan-500/30 rounded-lg text-cyan-400"
                  >
                    <Eye className="w-5 h-5" />
                  </a>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-slate-400 py-4">
              Nessun documento disponibile. L'amministratore caricherà i contenuti per voi.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizationPortal;
